import { useMemo, useRef, useState } from "react";
import axios from "axios";
import Loading from "src/components/Loading";
import { Option } from "src/components/ui/TGSelect";
import { useAppSelector } from "src/hooks/redux";
import { modifiedItemsState, selectItems } from "src/modules/spItems/selectors";
import {
  approversCardDocumentListName,
  collectionsListName,
  historyChangeFieldsDocumentListName,
  myDocumentsListName,
  versionsByDocumentListName,
} from "src/api/config";
import { emailQueueListName } from "src/api/config/email.config";
import { docStatuses, listMap } from "src/api/config/systemSwitch";
import { getItemsRequest } from "src/modules/spItems/api";
import { strapiUrl } from "src/modules/users/constants";
import ChangeResponsibleReviewModal, {
  CollectionOption,
} from "./ChangeResponsibleReviewModal";
import SelectDocumentsModal, {
  ChangeResponsibleDocument,
} from "./selectDocumentsModal";
import ChangeResponsibleSuccessModal from "./ChangeResponsibleSuccessModal";

type ActiveModal = "review" | "selectDocuments" | "success" | null;

type ChangeResponsibleProps = {
  open: boolean;
  onClose: () => void;
};

const getUserPersonalNumber = (user: any) =>
  user?.abiPersonnelNumber ||
  user?.PersonalNumber ||
  user?.EmployeeNumber ||
  user?.UserProfileProperties?.find(
    (property: any) => property.Key === "abiPersonnelNumber",
  )?.Value ||
  user?.UserProfileProperties?.find(
    (property: any) => property.Key === "EmployeeNumber",
  )?.Value ||
  "";

const getPreferredName = (user: any) => {
  const displayName = user?.DisplayName || user?.Title || user?.name || "";
  const parts = displayName.split(" ").filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1]}`;
  }

  return displayName;
};

const getResponsibleLabel = (user: any) => {
  const personalNumber = getUserPersonalNumber(user);
  const name = getPreferredName(user);

  return personalNumber ? `${name} (${personalNumber})` : name;
};

const replaceGreetingResponsible = (body: string, responsibleLabel: string) =>
  body.replace(/(Здравствуйте\s+)[^,]+(,)/, `$1${responsibleLabel}$2`);

const isEmailForDocument = (email: any, documentIds: Set<string>) => {
  const checkDoc = email?.CheckDoc ? String(email.CheckDoc) : "";

  if (checkDoc) {
    return documentIds.has(checkDoc);
  }

  return Array.from(documentIds).some((documentId) =>
    String(email?.Body || "").includes(`documentCard=${documentId}`),
  );
};

type UpdateListName =
  | typeof approversCardDocumentListName
  | typeof emailQueueListName
  | typeof myDocumentsListName
  | typeof versionsByDocumentListName;

type FailedUpdate = {
  ID: any;
  listName: UpdateListName;
  reason: unknown;
};

const UPDATE_BATCH_SIZE = 20;
const HISTORY_AUTHOR_ID = 15778;

type ResponsibleHistoryValue = {
  value: string;
  label: string;
  avatar: null;
  email?: string;
};

const getResponsibleHistoryValue = (
  responsible: Option | undefined,
  includeEmail = false,
): ResponsibleHistoryValue => {
  const email = responsible?.value?.Email || "";
  const historyValue: ResponsibleHistoryValue = {
    value: email,
    label: getResponsibleLabel(responsible?.value),
    avatar: null,
  };

  if (includeEmail) {
    historyValue.email = email;
  }

  return historyValue;
};

const createResponsibleHistoryChange = async (
  documentId: number,
  currentResponsible: Option | undefined,
  newResponsible: Option | undefined,
  isVersion = false,
) => {
  const listStrapiApi = listMap[historyChangeFieldsDocumentListName].replace(
    ":folder",
    "",
  );
  const JSONChangesFields = JSON.stringify({
    ResponsibleForRevision: {
      new: [getResponsibleHistoryValue(newResponsible)],
      old: [getResponsibleHistoryValue(currentResponsible, true)],
    },
  });

  const createdHistoryResponse = await axios.post(
    `${strapiUrl}${listStrapiApi}`,
    {
      data: {
        Title: `History fields card # ${documentId}`,
        IDDocument: documentId,
        JSONChangesFields,
        Statuses: [docStatuses.edited],
        VersionDocument: isVersion,
      },
    },
    { withCredentials: true },
  );
  const historyDocumentId = createdHistoryResponse.data?.data?.documentId;

  if (!historyDocumentId) {
    throw new Error(`Missing documentId for created history item ${documentId}`);
  }

  return axios.put(
    `${strapiUrl}${listStrapiApi}/${historyDocumentId}`,
    { data: { Author: HISTORY_AUTHOR_ID } },
    { withCredentials: true },
  );
};

const updateReceivedItems = async (
  listName: UpdateListName,
  items: any[],
  onItemUpdated?: (item: any) => Promise<unknown>,
) => {
  const listStrapiApi = listMap[listName].replace(":folder", "");
  const failedUpdates: FailedUpdate[] = [];

  for (let index = 0; index < items.length; index += UPDATE_BATCH_SIZE) {
    const batch = items.slice(index, index + UPDATE_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map((item) => {
        const { documentId, ID, ...data } = item;

        if (!documentId) {
          throw new Error(`Missing documentId for ${listName} item ${ID}`);
        }

        return axios
          .put(
            `${strapiUrl}${listStrapiApi}/${documentId}`,
            { data },
            { withCredentials: true },
          )
          .then((response) =>
            onItemUpdated ? onItemUpdated(item).then(() => response) : response,
          );
      }),
    );

    results.forEach((result, resultIndex) => {
      if (result.status === "rejected") {
        failedUpdates.push({
          ID: batch[resultIndex]?.ID,
          listName,
          reason: result.reason,
        });
      }
    });
  }

  return failedUpdates;
};

function ChangeResponsible({ open, onClose }: ChangeResponsibleProps) {
  const collections = useAppSelector((state) =>
    selectItems(state, collectionsListName),
  );
  const [activeModal, setActiveModal] = useState<ActiveModal>("review");
  const [selectedCollections, setSelectedCollections] = useState<
    CollectionOption[]
  >([]);
  const [currentResponsible, setCurrentResponsible] = useState<Option[]>([]);
  const [newResponsible, setNewResponsible] = useState<Option[]>([]);
  const hasInitializedCurrentResponsible = useRef(false);
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setActiveModal("review");
    setSelectedCollections([]);
    setCurrentResponsible([]);
    setNewResponsible([]);
    hasInitializedCurrentResponsible.current = false;
    setSelectedDocuments([]);
    setSubmitting(false);
    onClose();
  };

  const handleSubmitDocuments = async (
    documents: ChangeResponsibleDocument[],
  ) => {
    if (submitting) {
      return;
    }

    const currentResponsibleId = currentResponsible[0]?.value.Id;
    const currentResponsibleEmail = currentResponsible[0]?.value.Email;
    const newResponsibleEmail = newResponsible[0]?.value.Email;
    const newResponsibleLabel = getResponsibleLabel(newResponsible[0]?.value);
    const selectedDocumentsForUpdate = documents.filter(
      (document) => document.listName === myDocumentsListName,
    );
    const selectedVersionsForUpdate = documents.filter(
      (document) => document.listName === versionsByDocumentListName,
    );
    const selectedDocumentIds = new Set(
      selectedDocumentsForUpdate.map((document) => String(document.id)),
    );

    if (
      !currentResponsibleId ||
      !currentResponsibleEmail ||
      !newResponsibleEmail ||
      documents.length === 0
    ) {
      return;
    }

    setSubmitting(true);

    try {
      const documentsUpdateArr = selectedDocumentsForUpdate.map((document) => ({
        ID: document.id,
        documentId: document.documentId,
        ResponsibleForRevision: newResponsible[0]?.value.Id,
        ResponsibleForRevisionEmail: newResponsibleEmail,
      }));
      const versionsUpdateArr = selectedVersionsForUpdate.map((document) => ({
        ID: document.id,
        documentId: document.documentId,
        ResponsibleForRevision: newResponsible[0]?.value.Id,
        ResponsibleForRevisionEmail: newResponsibleEmail,
      }));
      let approversUpdateArr: any[] = [];
      let emailUpdateArr: any[] = [];

      if (documentsUpdateArr.length > 0) {
        const [approversResponse, queuedEmailsResponse] = await Promise.all([
          getItemsRequest({
            listName: approversCardDocumentListName,
            filters: {
              Statuses: [3],
              Approvers: [currentResponsibleId],
            },
          }),
          getItemsRequest({
            listName: emailQueueListName,
            filters: {
              Title: "пересмотр*",
              Recepients: `${currentResponsibleEmail}*`,
            },
          }),
        ]);

        const approversDocuments = modifiedItemsState(
          approversResponse.items,
          approversCardDocumentListName,
        );
        const queuedEmails = modifiedItemsState(
          queuedEmailsResponse.items,
          emailQueueListName,
        );
        const approversDocumentIdsById = new Map(
          approversResponse.items.map((item: any) => [
            item.ID,
            item.documentId,
          ]),
        );
        const queuedEmailDocumentIdsById = new Map(
          queuedEmailsResponse.items.map((item: any) => [
            item.ID,
            item.documentId,
          ]),
        );

        approversUpdateArr = approversDocuments.map((document) => {
          const approvers = Array.isArray(document.Approvers)
            ? document.Approvers
            : [];
          const updatedApprovers = approvers
            .filter(
              (approver) =>
                approver.ID !== Number(currentResponsibleId) &&
                approver.Email !== currentResponsibleEmail,
            )
            .map((user) => user.ID)
            .filter(Boolean);
          const newResponsibleId = newResponsible[0]?.value.Id;

          if (
            newResponsibleId &&
            !updatedApprovers.includes(newResponsibleId)
          ) {
            updatedApprovers.push(newResponsibleId);
          }

          return {
            ID: document.ID,
            documentId: approversDocumentIdsById.get(document.ID),
            Approvers: updatedApprovers,
          };
        });

        emailUpdateArr = queuedEmails
          .filter((email) => isEmailForDocument(email, selectedDocumentIds))
          .map((email) => ({
            ID: email.ID,
            documentId: queuedEmailDocumentIdsById.get(email.ID),
            Recepients: String(email.Recepients || "").replace(
              currentResponsibleEmail,
              newResponsibleEmail,
            ),
            Body: replaceGreetingResponsible(
              String(email.Body || ""),
              newResponsibleLabel,
            ),
            IsSent: false,
          }));
      }

      const updateRequests = [
        updateReceivedItems(myDocumentsListName, documentsUpdateArr, (item) =>
          createResponsibleHistoryChange(
            item.ID,
            currentResponsible[0],
            newResponsible[0],
            false,
          ),
        ),
        updateReceivedItems(
          versionsByDocumentListName,
          versionsUpdateArr,
          (item) =>
            createResponsibleHistoryChange(
              item.ID,
              currentResponsible[0],
              newResponsible[0],
              true,
            ),
        ),
      ];

      if (documentsUpdateArr.length > 0) {
        updateRequests.push(
          updateReceivedItems(approversCardDocumentListName, approversUpdateArr),
          updateReceivedItems(emailQueueListName, emailUpdateArr),
        );
      }

      const updateResults = await Promise.all(updateRequests);
      const failedUpdates = updateResults.flat();

      if (failedUpdates.length > 0) {
        console.error(
          "Failed to update change responsible items:",
          failedUpdates.map((item) => ({
            listName: item.listName,
            ID: item.ID,
            reason: item.reason,
          })),
        );
        return;
      }

      setActiveModal("success");
    } catch (error) {
      console.error("Error fetching change responsible submit data:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const collectionOptions = useMemo<CollectionOption[]>(() => {
    if (!Array.isArray(collections)) {
      return [];
    }

    return collections.map((collection) => ({
      id: String(collection.ID),
      name: collection.Title ?? "",
      value: collection,
    }));
  }, [collections]);

  if (!open) {
    return null;
  }

  return (
    <div className="changeResponsible">
      {activeModal === "review" && (
        <ChangeResponsibleReviewModal
          collections={collectionOptions}
          selectedCollections={selectedCollections}
          currentResponsible={currentResponsible}
          newResponsible={newResponsible}
          onChangeCollections={setSelectedCollections}
          onChangeCurrentResponsible={setCurrentResponsible}
          onChangeNewResponsible={setNewResponsible}
          hasInitializedCurrentResponsible={hasInitializedCurrentResponsible}
          onCancel={handleClose}
          onClose={handleClose}
          onSubmit={() => setActiveModal("selectDocuments")}
        />
      )}
      {activeModal === "selectDocuments" && (
        <SelectDocumentsModal
          selectedCollections={selectedCollections}
          currentResponsible={currentResponsible}
          selectedDocuments={selectedDocuments}
          submitting={submitting}
          onChangeSelectedDocuments={setSelectedDocuments}
          onBack={() => setActiveModal("review")}
          onSubmit={handleSubmitDocuments}
        />
      )}
      {activeModal === "success" && (
        <ChangeResponsibleSuccessModal onClose={handleClose} />
      )}
      {submitting && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-white/75">
          <Loading />
        </div>
      )}
    </div>
  );
}

export default ChangeResponsible;
